import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { styleMap } from 'lit/directives/style-map.js'
import { fromEvent, of, timer, zip } from 'rxjs'
import { filter, take, takeUntil, tap } from 'rxjs/operators'

export type SchmancySplashScreenDoneEvent = CustomEvent<void>

/**
 * Full-viewport splash overlay that dismisses once a minimum duration has
 * elapsed and (optionally) an external `ready` signal has fired. The splash
 * fades out while the underlying app content fades in.
 *
 * The `splash` slot is empty by default — pulls in no dependencies; the
 * consumer provides a logo, spinner, or animation.
 */
@customElement('schmancy-splash-screen')
export default class SchmancySplashScreen extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: flex;
				flex-direction: column;
				flex: 1 1 0;
				min-height: 0;
				position: relative;
			}

			.splash-layer {
				position: fixed;
				inset: 0;
				z-index: 50;
				overflow: hidden;
				display: flex;
				align-items: center;
				justify-content: center;
				background: var(--schmancy-splash-background, var(--schmancy-sys-color-surface-containerLowest, #000));
				transition: opacity var(--schmancy-splash-transition, 500ms) ease-out;
			}

			.content-layer {
				display: flex;
				flex-direction: column;
				flex: 1 1 0;
				min-height: 0;
				width: 100%;
				transition: opacity var(--schmancy-splash-transition, 500ms) ease-in-out;
			}
		`,
	]

	/**
	 * Minimum duration (ms) the splash layer stays visible. Prevents a flash
	 * when the app loads faster than expected.
	 */
	@property({ type: Number, attribute: 'min-duration' }) minDuration = 1500

	/**
	 * When true, the splash dismisses on the `minDuration` timer alone.
	 * When false (default), it additionally waits for an external ready signal
	 * (a `ready` event on this element, or a call to `this.ready()`).
	 */
	@property({ type: Boolean }) auto = false

	/**
	 * When true, the splash starts hidden. Use this for imperative control.
	 */
	@property({ type: Boolean, attribute: 'initially-hidden' }) initiallyHidden = false

	@state() private visible = true

	/**
	 * True once the dismiss fade has finished. Collapses the splash layer to
	 * `display:none` so heavy slotted splash visuals (a canvas/rAF animation
	 * like `<schmancy-waiting>`) stop intersecting the viewport and
	 * tear down — an opacity-only hide leaves the layer laid out fullscreen, so
	 * its IntersectionObserver keeps reporting visible and the loop runs forever
	 * behind the revealed app.
	 */
	@state() private dismissed = false

	@query('.splash-layer') private splashLayer!: HTMLElement

	connectedCallback() {
		super.connectedCallback()

		if (this.initiallyHidden) {
			this.visible = false
			this.dismissed = true
			return
		}

		const readySignal$ = this.auto ? of(null) : fromEvent<Event>(this, 'ready').pipe(take(1))
		const minTimer$ = timer(this.minDuration)

		zip(readySignal$, minTimer$)
			.pipe(
				take(1),
				tap(() => this.dismiss()),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	/** Signals the app is ready; the splash dismisses once minDuration has also elapsed. */
	public ready(): void {
		this.dispatchEvent(new Event('ready'))
	}

	/** Force the splash to show again (e.g. between route transitions). */
	public show(): void {
		this.dismissed = false
		this.visible = true
	}

	private dismiss(): void {
		this.visible = false
		this.dispatchEvent(
			new CustomEvent<void>('schmancy-splash-done', {
				bubbles: true,
				composed: true,
			}),
		)
	}

	/** Guarded on `opacity` + `!visible` so a re-`show()` (opacity 0→1) never
	 *  collapses the layer — only the fade-out direction does. */
	override firstUpdated(): void {
		fromEvent<TransitionEvent>(this.splashLayer, 'transitionend')
			.pipe(
				filter(e => e.propertyName === 'opacity' && !this.visible),
				takeUntil(this.disconnecting),
			)
			.subscribe(() => {
				this.dismissed = true
			})
	}

	render() {
		return html`
			<div
				class="splash-layer"
				aria-hidden=${!this.visible}
				style=${styleMap({
					opacity: this.visible ? '1' : '0',
					pointerEvents: this.visible ? 'auto' : 'none',
					display: this.dismissed ? 'none' : 'flex',
				})}
			>
				<slot name="splash"></slot>
			</div>
			<div
				class="content-layer"
				style=${styleMap({
					opacity: this.visible ? '0' : '1',
					pointerEvents: this.visible ? 'none' : 'auto',
				})}
			>
				<slot></slot>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-splash-screen': SchmancySplashScreen
	}
}
